import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Mic, Video, FlaskConical, ListChecks, Check, ChevronDown } from 'lucide-react';

const BUCKETS = [
  { key: 'unassigned', label: 'without variant', icon: FlaskConical, cls: 'text-amber-400 border-amber-900/50 bg-amber-950/20 hover:bg-amber-950/40', hint: 'assign a test variant' },
  { key: 'voice', label: 'waiting for voice', icon: Mic, cls: 'text-sky-400 border-sky-900/50 bg-sky-950/20 hover:bg-sky-950/40', hint: 'generate the ElevenLabs voice' },
  { key: 'video', label: 'waiting for video', icon: Video, cls: 'text-violet-400 border-violet-900/50 bg-violet-950/20 hover:bg-violet-950/40', hint: 'record the teleprompter video' },
];

export const OutreachProgressWidget = ({ onOpenLetter }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.getOutreachProgress().then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const { counts, items } = data;
  const openCount = (counts.unassigned || 0) + (counts.voice || 0) + (counts.video || 0);
  const total = openCount + (counts.ready || 0);
  if (total === 0) return null;
  const activeBucket = BUCKETS.find(b => b.key === open);
  const openItems = open ? (items[open] || []) : [];

  return (
    <div className="bg-card border border-border rounded-sm" data-testid="outreach-progress">
      <div className="px-5 py-3.5 flex items-center gap-2 flex-wrap">
        <ListChecks className="w-4 h-4 text-sky-400" />
        <span className="text-sm font-medium text-foreground">Production queue</span>
        <span className="text-[10px] font-mono text-muted-foreground ml-1" data-testid="progress-ready-count">
          {counts.ready || 0}/{total} ready
        </span>
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          {openCount === 0 ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-400 border border-emerald-900/40 bg-emerald-950/20 rounded-sm px-2.5 py-1" data-testid="progress-all-ready">
              <Check className="w-3 h-3" /> All letters ready — nothing to produce
            </span>
          ) : BUCKETS.map(b => {
            const n = counts[b.key] || 0;
            if (n === 0) return null;
            const Icon = b.icon;
            const active = open === b.key;
            return (
              <button key={b.key} onClick={() => setOpen(active ? null : b.key)}
                data-testid={`progress-chip-${b.key}`} title={b.hint}
                className={`inline-flex items-center gap-1.5 text-[11px] border rounded-sm px-2.5 py-1 transition-colors ${b.cls} ${active ? 'ring-1 ring-current' : ''}`}>
                <Icon className="w-3 h-3" /> {n} {b.label}
                <ChevronDown className={`w-3 h-3 transition-transform ${active ? 'rotate-180' : ''}`} />
              </button>
            );
          })}
        </div>
      </div>
      {open && activeBucket && (
        <div className="px-5 pb-3.5 border-t border-border/60 pt-3" data-testid={`progress-list-${open}`}>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
            Next step: {activeBucket.hint}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {openItems.map((it, i) => (
              <button key={`${it.letter_id}-${i}`} onClick={() => it.letter_id && onOpenLetter?.(it.letter_id)}
                data-testid={`progress-item-${it.letter_id}`}
                className="inline-flex items-center gap-1.5 text-[11px] text-foreground bg-secondary/40 hover:bg-secondary/70 border border-border/60 rounded-sm px-2.5 py-1 transition-colors">
                {it.company_name || 'Unnamed'}
                {(it.status === 'sent' || it.status === 'responded') && (
                  <span className="text-[9px] font-mono text-red-400 uppercase" title="Letter already sent — the QR code points to an unfinished page!">sent!</span>
                )}
              </button>
            ))}
            {openItems.length === 0 && <span className="text-[11px] text-muted-foreground">Nothing here.</span>}
          </div>
        </div>
      )}
    </div>
  );
};
